import { Collapse, Input, Tooltip, Typography } from 'antd';
import { isEqual } from 'lodash';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import 'src/app/components/Licenses/licenses.scss';
import { displayErrorMessage } from 'src/app/helpers/error.helper';
import { useAppSelector } from 'src/app/redux/hook';
import { SearchIcon } from 'src/icons';
import LinkIcon from 'src/icons/LinkIcon';

interface ILicensesProps {
  width: number;
  height: number;
}
const Licenses: React.FC<ILicensesProps> = ({ width, height }) => {
  const listRef = useRef<any>(null);
  const [licenseData, setLicenseData] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedLibrary, setSelectedLibrary] = useState<string>('');

  const { theme } = useAppSelector((state) => state.configContent);

  useEffect(() => {
    const setData = async () => {
      try {
        const urls = [
          'NGINR-CLIENT_3RD-PARTY-LICENSES.json',
          'NGINR-WEB-BACKEND_3RD-PARTY-LICENSES.json',
          'FORM_SERVER_3RD-PARTY-LICENSES.json',
          'PANDOC_SERVER_3RD-PARTY-LICENSES.json',
          'STORAGE_SERVER_3RD-PARTY-LICENSES.json',
          'LOGGER_SERVER_3RD-PARTY-LICENSES.json',
        ];

        const res = await Promise.all(urls.map((url) => fetch(url)));
        const resJson = await Promise.all(res.map((r) => r.json()));

        const mappedArray = [...resJson[0], ...resJson[1], ...resJson[2], ...resJson[3], ...resJson[4], ...resJson[5]];

        const unique: any[] = [];

        const nonUnique: any[] = [];

        for (let i = 0; i < mappedArray.length; i += 1) {
          if (!unique[mappedArray[i].library]) {
            nonUnique.push({ ...mappedArray[i], collapsed: false });
            unique[mappedArray[i].library] = 1;
          }
        }
        setLicenseData(nonUnique);
        setFilterData(nonUnique);
      } catch (error: any) {
        displayErrorMessage(error.message);
      }
    };
    setData();
  }, []);

  const [dataSource, setDataSource] = useState<any>({ data: [], hasMore: true });
  const [page, setPage] = useState(20);

  useEffect(() => {
    setDataSource({ data: [...filterData.slice(0, page)], hasMore: page !== filterData.length });
  }, [page, filterData]);

  // const toggleCollapsed = (library: string) => {
  //   setDataSource((prev: any) => {
  //     const foundIndex = prev.data.findIndex((item: any) => item.library === library);
  //     return {
  //       ...prev,
  //       data: [
  //         ...prev.data.slice(0, foundIndex),
  //         { ...prev.data[foundIndex], collapsed: !prev.data[foundIndex].collapsed },
  //         ...prev.data.slice(foundIndex + 1, prev.data.length),
  //       ],
  //     };
  //   });
  // };

  const itemData = useMemo(() => {
    return {
      items: dataSource.data,
      // toggleCollapsed,
      setSelectedLibrary,
      selectedLibrary,
    };
  }, [dataSource.data, selectedLibrary, setSelectedLibrary]);

  const getItemSize = (index: number) => {
    return dataSource.data[index]?.library === selectedLibrary ? 420 : 70;
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current._listRef.resetAfterIndex(0, true);
    }
  }, [selectedLibrary, dataSource.data]);

  const isItemLoaded = (idx: number) => {
    return !dataSource.hasMore || dataSource.data.length - 1 > idx;
  };

  useEffect(() => {
    setFilterData(licenseData.filter((item) => item.library.toLowerCase().includes(searchValue.toLowerCase())));
    setPage(20);
  }, [searchValue, licenseData]);

  return (
    <div>
      <Input
        id="search-input"
        suffix={<SearchIcon color="white" style={{ width: '18px', height: '18px' }} />}
        variant="borderless"
        style={{ borderBlock: `1px solid ${theme.colors.quoteBgc}`, color: 'white', borderRadius: '0px' }}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder="Search Library..."
      />
      <div style={{ paddingTop: '1rem' }}>
        <InfiniteLoader
          ref={listRef}
          isItemLoaded={isItemLoaded}
          itemCount={dataSource.hasMore ? dataSource.data.length + 1 : dataSource.data.length}
          loadMoreItems={() => {
            setPage((prev) => prev + 20);
          }}
          threshold={1}
        >
          {({ onItemsRendered, ref }) => (
            <VariableSizeList
              height={height}
              itemCount={dataSource.hasMore ? dataSource.data.length + 1 : dataSource.data.length}
              width={width}
              ref={ref}
              itemSize={(index) => getItemSize(index)}
              itemData={itemData}
              onItemsRendered={onItemsRendered}
            >
              {LicenseItem}
            </VariableSizeList>
          )}
        </InfiniteLoader>
      </div>
    </div>
  );
};

export default Licenses;

const LicenseItem = memo(({ index, style, data }: any) => {
  const [activeKey, setActiveKey] = useState<string[]>([]);
  const item = data.items[index];

  useEffect(() => {
    const key = data.selectedLibrary === item?.library ? [item.library] : [];
    setActiveKey(key);
  }, [data.selectedLibrary, item]);

  if (!item) return null;

  return (
    <div style={style}>
      <Collapse
        className="license-collapse"
        activeKey={activeKey}
        key={index}
        onChange={(idx) => {
          data.setSelectedLibrary(idx.length ? item.library : []);
        }}
        items={[
          {
            extra:
              item.repository !== 'N/A' ? (
                <Tooltip title={item.repository}>
                  <a
                    onClick={(e) => e.stopPropagation()}
                    style={{ paddingRight: '1rem', display: 'flex' }}
                    href={item.repository}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LinkIcon color="white" />
                  </a>
                </Tooltip>
              ) : null,
            key: item.library,
            label: (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <Typography.Text
                    style={{
                      color: 'rgba(255,255,255,0.92)',
                      fontWeight: 700,
                      fontSize: 15,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.library}
                  </Typography.Text>
                  <Typography.Text
                    style={{
                      color: 'rgba(255,255,255,0.72)',
                      fontSize: 12,
                      lineHeight: 1.2,
                      // whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.description === 'N/A' ? 'No Description Included for this Package' : item.description}
                  </Typography.Text>
                </div>
              </div>
            ),
            children: (
              <div
                style={{
                  background: 'rgba(0,0,0,0.18)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 14,
                  padding: 14,
                  height: '500px',
                  maxHeight: '35vh',
                  overflowY: 'auto',
                }}
              >
                <Typography.Text
                  style={{
                    color: 'rgba(255,255,255,0.92)',
                    fontWeight: 700,
                    fontSize: 15,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'center',
                  }}
                >
                  {item.licenseType}
                </Typography.Text>
                <Typography.Text style={{ color: 'rgba(255,255,255,0.72)', display: 'block', marginTop: 10 }}>
                  {item.licenseText}
                </Typography.Text>
              </div>
            ),
          },
        ]}
        style={{
          width: '100%',
          borderRadius: 18,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.5)',
          background: 'rgba(255,255,255,0.06) !important',
        }}
      />
    </div>
  );
}, isEqual);

LicenseItem.displayName = 'LicenseItem';
